import { StagingBanner } from '@a7y/ui/staging-banner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { CookieConsentCore } from '@/components/CookieConsentCore';
import LazyRoute from '@/components/ui/LazyRoute';
import { onConsentChange } from '@/lib/consent';
import { captureVia, onMarketingConsentGranted } from '@/lib/affiliate_tracking';
import { captureReferralOffer } from '@/lib/referral_offer';
import { MarketingRoutes } from './marketing-routes.client';
import { PUBLIC_CLIENT_ONLY_MARKETING_PATHS } from './marketing-route-registry';

const TtestPage = lazy(() => import('./pages/TtestPage'));
const CheckMyAppPage = lazy(() => import('./pages/CheckMyAppPage'));
const CheckMyAppResultPage = lazy(() => import('./pages/CheckMyAppResultPage'));
const ScheduleDemoRedirect = lazy(() => import('./pages/ScheduleDemoRedirect'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
// A7Y-6115: Weekly Bulletin double opt-in.
const NewsletterConfirmPage = lazy(() => import('./pages/newsletter/NewsletterConfirmPage'));
const NewsletterConfirmedPage = lazy(() => import('./pages/newsletter/NewsletterConfirmedPage'));
// A7Y-1897: affiliate portal (public — affiliates are NOT Clerk users).
// Ships DARK; reachable by direct URL only (no footer link — cutover ticket).
const AffiliateSignupPage = lazy(() => import('./pages/affiliate/AffiliateSignupPage'));
const AffiliateConfirmPage = lazy(() => import('./pages/affiliate/AffiliateConfirmPage'));
const AffiliateLoginPage = lazy(() => import('./pages/affiliate/AffiliateLoginPage'));
const AffiliateRedeemPage = lazy(() => import('./pages/affiliate/AffiliateRedeemPage'));
const AffiliateDashboardPage = lazy(() => import('./pages/affiliate/AffiliateDashboardPage'));
const PublicSignUpRoute = lazy(() =>
  import('./public-clerk-routes').then((module) => ({ default: module.PublicSignUpRoute }))
);
const PublicSignInRoute = lazy(() =>
  import('./public-clerk-routes').then((module) => ({ default: module.PublicSignInRoute }))
);
const PublicEmailPreferencesRoute = lazy(() =>
  import('./public-clerk-routes').then((module) => ({
    default: module.PublicEmailPreferencesRoute,
  }))
);
const PublicEmailCadenceRoute = lazy(() =>
  import('./public-clerk-routes').then((module) => ({
    default: module.PublicEmailCadenceRoute,
  }))
);

const publicQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Re-exported from the shared route registry (the single source of truth also
// consumed by the prerender build and the edge worker's 404 logic).
export { PUBLIC_CLIENT_ONLY_MARKETING_PATHS };

export function PublicMarketingApp() {
  // A7Y-1895: first-party affiliate tracking (sole attribution).
  useEffect(() => {
    captureVia();
    // A7Y-2814: copy the edge worker's offer meta tag into the same-tab
    // session cache. Read-only, no network, no durable write.
    captureReferralOffer();
    const unsubscribe = onConsentChange((state) => {
      if (state.marketing) void onMarketingConsentGranted();
    });
    return unsubscribe;
  }, []);

  return (
    <QueryClientProvider client={publicQueryClient}>
      <BrowserRouter>
        <StagingBanner />
        <Routes>
          {MarketingRoutes()}
          <Route path="/ttest" element={<PublicLazyRoute><TtestPage /></PublicLazyRoute>} />
          <Route
            path="/check-my-app"
            element={
              <PublicLazyRoute>
                <CheckMyAppPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/check-my-app/r/:token"
            element={
              <PublicLazyRoute>
                <CheckMyAppResultPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/schedule-demo"
            element={
              <PublicLazyRoute>
                <ScheduleDemoRedirect redirectUrl="https://demo.apptonomy.ai" />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/newsletter/confirm"
            element={
              <PublicLazyRoute>
                <NewsletterConfirmPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/newsletter/confirmed"
            element={
              <PublicLazyRoute>
                <NewsletterConfirmedPage />
              </PublicLazyRoute>
            }
          />
          {/* A7Y-1897: affiliate portal (dark, direct-URL only). */}
          <Route
            path="/affiliates/signup"
            element={
              <PublicLazyRoute>
                <AffiliateSignupPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/affiliates/confirm"
            element={
              <PublicLazyRoute>
                <AffiliateConfirmPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/affiliates/login"
            element={
              <PublicLazyRoute>
                <AffiliateLoginPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/affiliates/redeem"
            element={
              <PublicLazyRoute>
                <AffiliateRedeemPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/affiliates/dashboard"
            element={
              <PublicLazyRoute>
                <AffiliateDashboardPage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicLazyRoute>
                <PublicSignUpRoute />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/sign-in"
            element={
              <PublicLazyRoute>
                <PublicSignInRoute />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/unsubscribe"
            element={
              <PublicLazyRoute>
                <UnsubscribePage />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/email-preferences"
            element={
              <PublicLazyRoute>
                <PublicEmailPreferencesRoute />
              </PublicLazyRoute>
            }
          />
          <Route
            path="/email-cadence"
            element={
              <PublicLazyRoute>
                <PublicEmailCadenceRoute />
              </PublicLazyRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsentCore authStateLoaded={true} isSignedIn={false} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function PublicLazyRoute({ children }: { children: ReactNode }) {
  return <LazyRoute>{children}</LazyRoute>;
}
